// Phase 5 (web revival) — Stream D.
//
// Split-view editor. Left pane: a server-rendered preview (kicked off
// via debounced POST /v1/assets/{id}/edit/preview on every ops change,
// blob-URL'd into an <img>). Right pane: rotate / crop / color / filter
// controls owned by OpsPanel.
//
// State machine notes:
// - `ops` is the single source of truth for the in-flight edit. The
//   panel mutates it, the preview effect debounces+POSTs it, and the
//   save buttons ship it as-is.
// - On mount we fetch the user's existing edit row (if any) and hydrate
//   `ops` so they pick up where they left off. The initial <img> falls
//   back to AssetDetailDTO.thumbnails.large so the pane isn't empty
//   while we wait for the first preview blob.
// - Preview requests are debounced 250ms after the last ops mutation,
//   and we use an AbortController so a fresh ops change cancels the
//   in-flight render — otherwise a slow request that finishes after a
//   fast follow-up would overwrite the fresh frame with stale pixels.
// - Each new preview revokes the prior blob URL via URL.revokeObjectURL
//   so we don't leak object URLs across edits.
//
// What's NOT here (simplified vs. the full spec):
// - Visual drag-handles for crop: punted to v2. Today the crop UI is
//   just four numeric inputs in [0,1] (see components/edit/CropOverlay).
// - No undo/redo stack — the panel's controls already let the user dial
//   any slider back to zero, and "Discard" wipes the row entirely.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, apiRequest, ApiError } from '../api/client';
import { qk } from '../app/queryClient';
import { snapshot } from '../app/auth';
import type {
  AssetDetailDTO,
  BakeDispatchResponse,
  EditDTO,
  EditOp,
  EditsListResponse,
} from '../api/types';
import { EditorPreview } from '../components/edit/EditorPreview';
import { OpsPanel } from '../components/edit/OpsPanel';

const PREVIEW_DEBOUNCE_MS = 250;

async function fetchPreview(
  assetID: string,
  ops: EditOp[],
  signal: AbortSignal,
): Promise<string> {
  const resp = await apiRequest<Response>(`/v1/assets/${assetID}/edit/preview`, {
    method: 'POST',
    body: { ops },
    accept: 'image/jpeg',
    signal,
  });
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

export function EditPage({ assetID }: { assetID: string }) {
  const navigate = useNavigate();
  const currentUserID = snapshot().user?.id ?? null;

  // ─── data loads ────────────────────────────────────────────────────
  const detail = useQuery({
    queryKey: qk.asset(assetID),
    queryFn: () => api.get<AssetDetailDTO>(`/v1/assets/${assetID}`),
  });
  const editsQuery = useQuery({
    queryKey: qk.assetEdits(assetID),
    queryFn: () => api.get<EditsListResponse>(`/v1/assets/${assetID}/edits`),
  });

  // ─── local edit state ──────────────────────────────────────────────
  const [ops, setOps] = useState<EditOp[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<
    | { kind: 'idle' }
    | { kind: 'saving' }
    | { kind: 'saved' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  const [bakeOpen, setBakeOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  // ─── hydrate ops from the user's existing edit (once) ─────────────
  const existingEdit: EditDTO | null = useMemo(() => {
    if (!editsQuery.data || !currentUserID) return null;
    return (
      editsQuery.data.edits.find((e) => e.author_id === currentUserID) ?? null
    );
  }, [editsQuery.data, currentUserID]);

  useEffect(() => {
    if (hydrated) return;
    if (!editsQuery.isSuccess) return;
    if (existingEdit) {
      setOps(existingEdit.ops);
    }
    setHydrated(true);
  }, [hydrated, editsQuery.isSuccess, existingEdit]);

  // ─── seed the preview pane with thumbnails.large until first POST ─
  useEffect(() => {
    if (previewURL !== null) return;
    if (!detail.data) return;
    setPreviewURL(detail.data.thumbnails.large);
  }, [detail.data, previewURL]);

  // ─── debounced preview render ─────────────────────────────────────
  // We track the most recently created blob URL in a ref so the unmount
  // cleanup can revoke whatever's live without racing the state setter.
  const blobURLRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hydrated) return;
    if (!detail.data) return;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setPreviewLoading(true);
      setPreviewError(null);
      fetchPreview(assetID, ops, controller.signal)
        .then((url) => {
          // Revoke the previous URL (if it was a blob:) before swapping.
          const prev = blobURLRef.current;
          if (prev && prev.startsWith('blob:')) {
            URL.revokeObjectURL(prev);
          }
          blobURLRef.current = url;
          setPreviewURL(url);
          setPreviewLoading(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setPreviewLoading(false);
          if (err instanceof ApiError) {
            setPreviewError(err.reason);
          } else if (err instanceof Error) {
            setPreviewError(err.message);
          } else {
            setPreviewError('Preview failed.');
          }
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [ops, assetID, hydrated, detail.data]);

  // Final cleanup: revoke any live blob URL when the page unmounts.
  useEffect(() => {
    return () => {
      const url = blobURLRef.current;
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  // ─── save handlers ────────────────────────────────────────────────
  async function handleSave() {
    setSaveState({ kind: 'saving' });
    try {
      await api.put<EditDTO>(`/v1/assets/${assetID}/edit`, {
        ops,
        make_primary: true,
      });
      setSaveState({ kind: 'saved' });
      window.setTimeout(() => {
        setSaveState((s) => (s.kind === 'saved' ? { kind: 'idle' } : s));
      }, 1800);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.reason
          : err instanceof Error
            ? err.message
            : 'Save failed.';
      setSaveState({ kind: 'error', message });
    }
  }

  async function handleBake(replacePrimary: boolean) {
    setBakeOpen(false);
    setSaveState({ kind: 'saving' });
    try {
      await api.post<BakeDispatchResponse>(`/v1/assets/${assetID}/edit/bake`, {
        ops,
        replace_primary: replacePrimary,
      });
      setSaveState({ kind: 'idle' });
      // WS asset.baked will reconcile the detail cache. Bounce back to
      // the asset detail page so the user sees the new resource land.
      navigate({ to: '/asset/$assetID', params: { assetID } });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.code === 'edit.too_many_bakes'
            ? 'You already have several bakes in flight. Try again in a moment.'
            : err.reason
          : err instanceof Error
            ? err.message
            : 'Bake failed.';
      setSaveState({ kind: 'error', message });
    }
  }

  async function handleDiscard() {
    setDiscardOpen(false);
    setSaveState({ kind: 'saving' });
    try {
      await api.delete<void>(`/v1/assets/${assetID}/edit`);
      setOps([]);
      setSaveState({ kind: 'idle' });
      // Friendlier: bounce back to the asset.
      navigate({ to: '/asset/$assetID', params: { assetID } });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.reason
          : err instanceof Error
            ? err.message
            : 'Discard failed.';
      setSaveState({ kind: 'error', message });
    }
  }

  // ─── render ───────────────────────────────────────────────────────
  if (detail.isLoading || editsQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-neutral-950">
        <p className="text-sm text-neutral-500">Loading editor…</p>
      </div>
    );
  }

  if (detail.isError) {
    return (
      <div className="flex h-full items-center justify-center bg-neutral-950">
        <p className="text-sm text-red-300">
          {detail.error instanceof ApiError
            ? detail.error.reason
            : 'Failed to load asset.'}
        </p>
      </div>
    );
  }

  const saving = saveState.kind === 'saving';

  return (
    <div className="flex h-full flex-col bg-neutral-950">
      <div className="flex min-h-0 flex-1">
        <div className="relative flex-1">
          <EditorPreview src={previewURL} loading={previewLoading} />
          {previewError && (
            <div
              role="alert"
              className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-md border border-red-900/60 bg-red-950/80 px-3 py-1.5 text-xs text-red-200 shadow-md"
            >
              Preview: {previewError}
            </div>
          )}
        </div>
        <OpsPanel ops={ops} onOpsChange={setOps} />
      </div>

      <footer className="flex items-center justify-between gap-3 border-t border-neutral-800 bg-neutral-900 px-4 py-3">
        <div className="text-xs text-neutral-400">
          {saveState.kind === 'saved' && (
            <span className="rounded-md border border-emerald-900/60 bg-emerald-950/40 px-2 py-0.5 text-emerald-300">
              Saved
            </span>
          )}
          {saveState.kind === 'error' && (
            <span className="rounded-md border border-red-900/60 bg-red-950/40 px-2 py-0.5 text-red-300">
              {saveState.message}
            </span>
          )}
          {saveState.kind === 'idle' && ops.length > 0 && (
            <span>
              {ops.length} op{ops.length === 1 ? '' : 's'} pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDiscardOpen(true)}
            disabled={saving || (!existingEdit && ops.length === 0)}
            className="rounded-md border border-red-900/70 bg-red-950/30 px-3 py-1.5 text-xs font-medium text-red-200 transition-colors hover:bg-red-900/40 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={() => setBakeOpen(true)}
            disabled={saving || ops.length === 0}
            className="rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-xs font-medium text-neutral-200 transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Save copy (bake)
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || ops.length === 0}
            className="rounded-md bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-900 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saveState.kind === 'saving' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </footer>

      {bakeOpen && (
        <BakeModal
          onCancel={() => setBakeOpen(false)}
          onConfirm={handleBake}
        />
      )}
      {discardOpen && (
        <ConfirmModal
          title="Discard edit?"
          body="This deletes your current edit row for this asset. Other authors' edits are not affected."
          confirmLabel="Discard"
          destructive
          onCancel={() => setDiscardOpen(false)}
          onConfirm={handleDiscard}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Modals — simple centered cards on a backdrop. Not portals (no need
// for the overhead at this footprint) and not focus-trapped (single
// confirm button + cancel is the entire surface).
// ──────────────────────────────────────────────────────────────────────

function ModalShell({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm rounded-lg border border-neutral-800 bg-neutral-900 p-5 shadow-xl">
        {children}
      </div>
    </div>
  );
}

function ConfirmModal({
  title,
  body,
  confirmLabel,
  destructive,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const confirmClass = destructive
    ? 'rounded-md border border-red-900/70 bg-red-950/40 px-3 py-1.5 text-xs font-medium text-red-200 hover:bg-red-900/50'
    : 'rounded-md bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-900 hover:bg-white';
  return (
    <ModalShell>
      <h2 className="mb-2 text-sm font-semibold text-neutral-100">{title}</h2>
      <p className="mb-4 text-xs text-neutral-400">{body}</p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-xs text-neutral-300 hover:bg-neutral-800"
        >
          Cancel
        </button>
        <button type="button" onClick={onConfirm} className={confirmClass}>
          {confirmLabel}
        </button>
      </div>
    </ModalShell>
  );
}

function BakeModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (replacePrimary: boolean) => void;
}) {
  const [replacePrimary, setReplacePrimary] = useState(false);
  return (
    <ModalShell>
      <h2 className="mb-2 text-sm font-semibold text-neutral-100">
        Bake creates a new file.
      </h2>
      <p className="mb-3 text-xs text-neutral-400">
        Baking applies the current ops permanently into a new resource. You
        can optionally replace the asset's primary file.
      </p>
      <label className="mb-4 flex items-center gap-2 text-xs text-neutral-200">
        <input
          type="checkbox"
          checked={replacePrimary}
          onChange={(e) => setReplacePrimary(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-neutral-700 bg-neutral-950"
        />
        Replace primary
      </label>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-neutral-800 bg-neutral-950 px-3 py-1.5 text-xs text-neutral-300 hover:bg-neutral-800"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm(replacePrimary)}
          className="rounded-md bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-900 hover:bg-white"
        >
          Bake
        </button>
      </div>
    </ModalShell>
  );
}
